import {
  CardContent,
  Typography,
  Button,
  Box,
  Zoom,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAppContext } from '../context/AppContext';
import { useState, useEffect } from "react";
import { financialService } from '../utils/api';
import CONFIG from "../utils/config";

const initCalendly = () => {
  if (window.Calendly) {
    window.Calendly.initPopupWidget({
      url: CONFIG.calendly_link
    });
  }
};

const LastPage = () => {
  const { state, actions } = useAppContext();
  const [showDefaultThankYou, setShowDefaultThankYou] = useState(state.requestCallback);
  const [showFinalMinimal, setShowFinalMinimal] = useState(false);
  const [userChecked, setUserChecked] = useState(false);
  const [meetsThreshold, setMeetsThreshold] = useState(false);


  const calculateLiquidDHI = () => {
    const liquidDHIComponents = [
      state.bankBalance || 0,
      state.fixedDeposit || 0,
      state.bonds || 0,
      state.globalBonds || 0,
      state.debtMF || 0,
      state.hybridMF || 0,
    ];
    
    return liquidDHIComponents.reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0);
  };
 
  const calculateNetworth = () => {
    const NetworthComponents = [
      state.bankBalance || 0,
      state.fixedDeposit || 0,
      state.bonds || 0,
      state.debtMF || 0,
      state.equityMF || 0,
      state.hybridMF || 0,
      state.invits || 0,
      state.stocks || 0,
      state.pms || 0,
      state.globalEquity || 0,
      state.globalMF || 0,
      state.globalBonds || 0,
      state.gold || 0,
      state.otherInvestments || 0,
      state.retirementFund || 0,
      state.unlisted || 0,
      state.realEstate || 0,
    ];
    
    return NetworthComponents.reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0);
  };

  const calculateLiquidNetworth = () => {
    const liquidNetworthComponents = [
      state.bankBalance || 0,
      state.fixedDeposit || 0,
      state.bonds || 0,
      state.debtMF || 0,
      state.equityMF || 0,
      state.hybridMF || 0,
      state.invits || 0,
      state.stocks || 0,
      state.pms || 0,
      state.globalEquity || 0,
      state.globalMF || 0,
      state.globalBonds || 0,
      state.gold || 0,
      state.otherInvestments || 0,
    ];
    
    return liquidNetworthComponents.reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0);
  };

  // Check thresholds when component mounts or state changes
  useEffect(() => {
    const liquidDHI = calculateLiquidDHI();
    const liquidNetworth = calculateLiquidNetworth();
    const netWorth = calculateNetworth();
    
    //const meetsRequirement = liquidDHI >= CONFIG.liquidDHI_Qualification || liquidNetworth >= CONFIG.liquidNetworth_Qualification || netWorth >= CONFIG.networth_Qualification;
    const meetsRequirement = liquidDHI >= CONFIG.liquidDHI_Qualification;
    //const meetsRequirement = true;
    setMeetsThreshold(meetsRequirement);
    
    // If threshold is not met, directly show final minimal message
    if (!meetsRequirement) {
      // console.log('User does not meet minimum threshold requirements');
      setShowFinalMinimal(true);
    }
  }, [state]); // Re-run when state changes

  const scheduleCallClick = async () => {
    initCalendly()
    await financialService.requestCallback({
      "phoneNumber": state.phoneNumber,
      "lastName": state.clientName,
      "emailId": state.email,
      "requestCallback": true,
      "scheduleMeeting": true,
    })
  }

  const handleSubmit = async () => {
    if (userChecked) {
      actions.updateFormData('requestCallback', true);
      setShowDefaultThankYou(true);
      await financialService.requestCallback({
        "phoneNumber": state.phoneNumber,
        "lastName": state.clientName,
        "emailId": state.email,
        "requestCallback": true,
        "scheduleMeeting": false,
      })
    } else {
      setShowFinalMinimal(true);
    }
  };

  return (
    <CardContent>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          my: -2
        }}
      >
        <Zoom in={true}>
          <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
        </Zoom>

        {showDefaultThankYou ? (
          <>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Thank You, {state.clientName.split(" ")[0]}!
            </Typography>
            <Typography variant="h6" sx={{ mb: 2 }}>
              We now have everything needed to determine your personalized asset allocation aligned with your goals and risk tolerance.
            </Typography>
            <Typography variant="h6" sx={{ mb: 2 }}>
              We will be in touch shortly to present this to you.
            </Typography>
            <Typography variant="h6" sx={{ mb: 2 }}>
              We thank you for planning your goals with Marcellus.
            </Typography>
          </>
        ) : showFinalMinimal ? (
          <Typography variant="h6" sx={{ mb: 2 }}>
            Thank You for planning your goals with Marcellus.
          </Typography>
        ) : meetsThreshold ? (
          <>
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
              You have now planned your goals and seen your existing asset allocation.
            </Typography>
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
              But are the two aligned?
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', color: 'red'}}>
            Schedule a virtual meeting with our investment counsellors to review your asset allocation.
            </Typography>

            {/* Calendly button */}
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={scheduleCallClick}
              sx={{ py: 1.5, px: 4, borderRadius: 2, fontWeight: 'bold', mb: 2 }}
            >
              Schedule a Meeting
            </Button>
            {/* <Typography variant="body2" sx={{ mb: 3, textAlign: 'center' }}>
              Can’t find a slot? Request a callback instead.
            </Typography> */}

            {/* Request Callback option */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={userChecked}
                  onChange={(e) => setUserChecked(e.target.checked)}
                />
              }
              // label="Select this option if you would like us to determine your optimal asset allocation – aligned with your goals and risk tolerance."
              label="Can’t find a slot? Request a callback instead."
              sx={{ mb: 3 }}
            />

            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleSubmit}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2,
                fontWeight: 'bold'
              }}
            >
              SUBMIT
            </Button>
          </>
        ) : null}
      </Box>
    </CardContent>
  );
};

export default LastPage;
